import type { ReactElement } from "react";
import { Button, type ButtonSize } from "@design-system/Button/Button";
import { VStack } from "@design-system/VStack/VStack";
import { ArrowDownIcon, DotsHorizontalIcon, DownloadIcon } from "@icons";
import {
    buttonVariants,
    defaultSizeByVariant,
    sizeOptionsByVariant,
    type ButtonVariant,
} from "./ButtonPlayground.lib";
import { BooleanControl, SelectControl } from "./playgroundControls";

/** The stage trigger: any `Button` element, or the `Menu.More` kebab preset. */
export type MenuPlaygroundTriggerKind = "button" | "more";

export interface MenuPlaygroundTriggerOptions {
    kind: MenuPlaygroundTriggerKind;
    variant: ButtonVariant;
    size: ButtonSize;
    isIconOnly: boolean;
    hasLeftIcon: boolean;
    hasChevron: boolean;
}

export const defaultTriggerOptions: MenuPlaygroundTriggerOptions = {
    kind: "button",
    variant: "secondary",
    size: "md",
    isIconOnly: false,
    hasLeftIcon: true,
    hasChevron: false,
};

const moreVariants = ["secondary", "primary"] as const;
type MoreVariant = (typeof moreVariants)[number];

const isMoreVariant = (variant: ButtonVariant): variant is MoreVariant =>
    moreVariants.some((option) => option === variant);

/** Sizes the current kind and variant accept; `Menu.More` has no `xs`. */
const sizeOptionsFor = (
    options: MenuPlaygroundTriggerOptions,
): readonly ButtonSize[] => {
    const sizes: readonly ButtonSize[] = sizeOptionsByVariant[options.variant];
    return options.kind === "more"
        ? sizes.filter((size) => size !== "xs")
        : sizes;
};

/** Keeps `size` legal after the kind or variant changes. */
const withLegalSize = (
    options: MenuPlaygroundTriggerOptions,
): MenuPlaygroundTriggerOptions => {
    const sizes = sizeOptionsFor(options);
    if (sizes.includes(options.size)) {
        return options;
    }
    const fallback = defaultSizeByVariant[options.variant];
    return { ...options, size: sizes.includes(fallback) ? fallback : sizes[0] };
};

const kindOptions: ReadonlyArray<{
    value: MenuPlaygroundTriggerKind;
    label: string;
}> = [
    { value: "button", label: "Button element" },
    { value: "more", label: "Menu.More (kebab)" },
];

/** The aside's Trigger section: kind, then the Button's variant, size and slots. */
export const MenuPlaygroundTriggerControls = ({
    options,
    onOptionsChange,
}: {
    options: MenuPlaygroundTriggerOptions;
    onOptionsChange: (options: MenuPlaygroundTriggerOptions) => void;
}) => {
    const update = (patch: Partial<MenuPlaygroundTriggerOptions>) =>
        onOptionsChange(withLegalSize({ ...options, ...patch }));
    const variantOptions = (
        options.kind === "more" ? moreVariants : buttonVariants
    ).map((variant) => ({ value: variant, label: variant }));
    const isButton = options.kind === "button";

    return (
        <section>
            <h2>Trigger</h2>
            <VStack gap={10}>
                <SelectControl<MenuPlaygroundTriggerKind>
                    label="Kind"
                    options={kindOptions}
                    value={options.kind}
                    onChange={(kind) =>
                        update(
                            kind === "more" && !isMoreVariant(options.variant)
                                ? { kind, variant: "secondary" }
                                : { kind },
                        )
                    }
                />
                <SelectControl<ButtonVariant>
                    label="Variant"
                    options={variantOptions}
                    value={options.variant}
                    onChange={(variant) => update({ variant })}
                />
                <SelectControl<ButtonSize>
                    label="Size"
                    options={sizeOptionsFor(options).map((size) => ({
                        value: size,
                        label: size,
                    }))}
                    value={options.size}
                    onChange={(size) => update({ size })}
                />
                <BooleanControl
                    label="Icon only"
                    checked={isButton && options.isIconOnly}
                    disabled={!isButton}
                    onChange={(isIconOnly) => update({ isIconOnly })}
                />
                <BooleanControl
                    label="Left icon"
                    checked={isButton && options.hasLeftIcon}
                    disabled={!isButton || options.isIconOnly}
                    onChange={(hasLeftIcon) => update({ hasLeftIcon })}
                />
                <BooleanControl
                    label="Chevron (right icon)"
                    checked={isButton && options.hasChevron}
                    disabled={!isButton || options.isIconOnly}
                    onChange={(hasChevron) => update({ hasChevron })}
                />
            </VStack>
        </section>
    );
};

/**
 * The stage's Button element trigger. The switch narrows `size` to what each
 * variant's type accepts, so no cast is needed.
 */
export const buildTriggerButton = (
    options: MenuPlaygroundTriggerOptions,
): ReactElement => {
    const slotProps = options.isIconOnly
        ? ({ iconOnly: true, "aria-label": "Invoice actions" } satisfies {
              iconOnly: true;
              "aria-label": string;
          })
        : {
              leftIcon: options.hasLeftIcon ? <DownloadIcon /> : undefined,
              rightIcon: options.hasChevron ? <ArrowDownIcon /> : undefined,
          };
    const label = options.isIconOnly ? (
        <DotsHorizontalIcon />
    ) : (
        "Invoice actions"
    );
    const { size } = options;

    switch (options.variant) {
        case "primary":
        case "secondary":
            return (
                <Button variant={options.variant} size={size} {...slotProps}>
                    {label}
                </Button>
            );
        case "tertiary":
        case "destructive":
            return (
                <Button
                    variant={options.variant}
                    size={size === "xs" ? "sm" : size}
                    {...slotProps}
                >
                    {label}
                </Button>
            );
        case "ghost":
            return (
                <Button variant="ghost" size="md" {...slotProps}>
                    {label}
                </Button>
            );
        case "accent":
            return (
                <Button variant="accent" size="md" {...slotProps}>
                    {label}
                </Button>
            );
        case "link":
            return (
                <Button
                    variant="link"
                    size={size === "xs" ? "xs" : "sm"}
                    {...slotProps}
                >
                    {label}
                </Button>
            );
    }
};

/** The `Menu.More` preset's own props for the current options. */
export const moreTriggerProps = (options: MenuPlaygroundTriggerOptions) => ({
    variant: isMoreVariant(options.variant) ? options.variant : "secondary",
    size: options.size === "xs" ? "sm" : options.size,
});
